import routes, { getCustomerDetailsPageUrl, getMasterSequencePageUrl } from 'router/routes'
import T from 'T'
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_ENTITY_TYPE,
  NOTIFICATION_SEVERITY,
  NOTIFICATION_SOURCE,
  NOTIFICATION_STATUS,
  isUnread,
  type Notification,
  type NotificationCategory,
  type NotificationEntityType,
  type NotificationSeverity,
  type NotificationSource,
  type NotificationStatus,
} from 'types/modules/notification-center/notification'

/** Theme palette family per status — resolved against the theme at render time, never a literal. */
export const STATUS_PALETTE: Readonly<Record<NotificationStatus, 'success' | 'error' | 'warning' | 'info' | 'grey'>> = Object.freeze({
  [NOTIFICATION_STATUS.QUEUED]: 'grey',
  [NOTIFICATION_STATUS.RUNNING]: 'info',
  [NOTIFICATION_STATUS.SUCCEEDED]: 'success',
  [NOTIFICATION_STATUS.FAILED]: 'error',
  [NOTIFICATION_STATUS.PARTIAL]: 'warning',
})

export const STATUS_LABELS: Readonly<Record<NotificationStatus, string>> = Object.freeze({
  [NOTIFICATION_STATUS.QUEUED]: 'Queued',
  [NOTIFICATION_STATUS.RUNNING]: 'Running',
  [NOTIFICATION_STATUS.SUCCEEDED]: 'Succeeded',
  [NOTIFICATION_STATUS.FAILED]: 'Failed',
  [NOTIFICATION_STATUS.PARTIAL]: 'Partial',
})

/** Severity a job's status implies when `payload.severity` is absent. */
const SEVERITY_BY_STATUS: Readonly<Record<NotificationStatus, NotificationSeverity>> = Object.freeze({
  [NOTIFICATION_STATUS.QUEUED]: NOTIFICATION_SEVERITY.INFO,
  [NOTIFICATION_STATUS.RUNNING]: NOTIFICATION_SEVERITY.INFO,
  [NOTIFICATION_STATUS.SUCCEEDED]: NOTIFICATION_SEVERITY.SUCCESS,
  [NOTIFICATION_STATUS.FAILED]: NOTIFICATION_SEVERITY.ERROR,
  [NOTIFICATION_STATUS.PARTIAL]: NOTIFICATION_SEVERITY.WARNING,
})

/**
 * How urgent an event is: an explicit `payload.severity` wins, otherwise a job
 * derives it from `status`. Pairs with the category icon so one mark carries two
 * facts — how urgent, and what kind.
 */
export const severityOf = (notification: Notification): NotificationSeverity =>
  notification.payload?.severity ?? SEVERITY_BY_STATUS[notification.status] ?? NOTIFICATION_SEVERITY.INFO

export const CATEGORY_LABELS: Readonly<Record<NotificationCategory, string>> = Object.freeze({
  [NOTIFICATION_CATEGORY.JOB]: 'Background job',
  [NOTIFICATION_CATEGORY.CUSTOMER_EVENT]: 'Customer event',
})

/**
 * CRM area each producer belongs to. Derived on the client because the contract's
 * `source` is a producer id and carries no area — the "By type" views need the
 * area, so this map is the seam if a column ever lands.
 */
export const AREA_BY_SOURCE: Readonly<Record<NotificationSource, string>> = Object.freeze({
  [NOTIFICATION_SOURCE.INVOICE_BATCH]: 'Billing',
  [NOTIFICATION_SOURCE.INVOICE_DELIVERY]: 'Billing',
  [NOTIFICATION_SOURCE.RENTAL_FEES]: 'Billing',
  [NOTIFICATION_SOURCE.SUSPENSION]: 'Billing',
  [NOTIFICATION_SOURCE.AUTOPAY]: 'Payments',
  [NOTIFICATION_SOURCE.PAYMENT_METHOD]: 'Payments',
  [NOTIFICATION_SOURCE.EXPORT]: 'Reporting',
  [NOTIFICATION_SOURCE.ACCOUNT_DEACTIVATION]: 'Customers',
  [NOTIFICATION_SOURCE.ACCOUNT_REACTIVATION]: 'Customers',
  [NOTIFICATION_SOURCE.SNAP_TO_ROAD]: 'Routing',
  [NOTIFICATION_SOURCE.ROUTE_OPTIMIZATION]: 'Routing',
})

/**
 * Producers with a live write path today. The contract declares nine; the rest are agreed but
 * unbuilt, and a view or a toggle for a producer that cannot fire reads as broken rather than as
 * forthcoming. One line per producer as each lands — that is the whole cost of restoring an area.
 */
export const LIVE_SOURCES: readonly NotificationSource[] = Object.freeze([
  NOTIFICATION_SOURCE.ACCOUNT_DEACTIVATION,
  NOTIFICATION_SOURCE.ACCOUNT_REACTIVATION,
])

/**
 * Preference keys for the live producers. The catalog is the server's, so these are matched by hand
 * against it and have to be extended alongside `LIVE_SOURCES` when a producer ships. Named because a
 * caller that promises a notification has to check the recipient's toggle for that producer.
 */
export const NOTIFICATION_PREF_KEY = {
  ACCOUNT_DEACTIVATION: 'job.account_deactivation',
  ACCOUNT_REACTIVATION: 'job.account_reactivation',
  ROUTE_OPTIMIZATION: 'job.route_optimization',
} as const

export const LIVE_PREF_KEYS: readonly string[] = Object.freeze([
  NOTIFICATION_PREF_KEY.ACCOUNT_DEACTIVATION,
  NOTIFICATION_PREF_KEY.ACCOUNT_REACTIVATION,
])

// Route optimization is live per tenant rather than per release, so it joins the set behind the optimizer's own gate.
const OPTIMIZER_SOURCES: readonly NotificationSource[] = Object.freeze([...LIVE_SOURCES, NOTIFICATION_SOURCE.ROUTE_OPTIMIZATION])

const OPTIMIZER_PREF_KEYS: readonly string[] = Object.freeze([...LIVE_PREF_KEYS, NOTIFICATION_PREF_KEY.ROUTE_OPTIMIZATION])

/** Only the areas the live producers write into, so "By type" never lists a view that stays empty. */
const areasOf = (sources: readonly NotificationSource[]): readonly string[] =>
  Object.freeze(sources.map(source => AREA_BY_SOURCE[source]).filter((area, index, areas) => areas.indexOf(area) === index))

export const sourcesForArea = (area: string): NotificationSource[] =>
  (Object.keys(AREA_BY_SOURCE) as NotificationSource[]).filter(source => AREA_BY_SOURCE[source] === area)

export const areaFor = (notification: Notification): string => AREA_BY_SOURCE[notification.source] ?? 'Other'

export const NOTIFICATION_VIEW = {
  ALL: 'ALL',
  UNREAD: 'UNREAD',
} as const

export type NotificationView = {
  key: string
  title: string
  match: (notification: Notification) => boolean
  /** Unread badges stay on the Inbox group only, per the prototype. */
  showBadge?: boolean
}

const areaView = (area: string): NotificationView => ({
  key: `AREA_${area.toUpperCase()}`,
  title: area,
  match: notification => areaFor(notification) === area,
})

const INBOX_VIEWS: readonly NotificationView[] = Object.freeze([
  { key: NOTIFICATION_VIEW.ALL, title: T.ALL_NOTIFICATIONS, match: () => true, showBadge: true },
  { key: NOTIFICATION_VIEW.UNREAD, title: T.UNREAD, match: isUnread, showBadge: true },
])

export type NotificationLiveSet = {
  areas: readonly string[]
  prefKeys: readonly string[]
  views: readonly NotificationView[]
  viewGroups: readonly { groupName: string; views: readonly NotificationView[] }[]
}

/**
 * "By activity" splits jobs from customer events, and every customer-event producer is still unbuilt —
 * the group would be a single entry duplicating the inbox. It comes back with the first one.
 */
const liveSetOf = (sources: readonly NotificationSource[], prefKeys: readonly string[]): NotificationLiveSet => {
  const areas = areasOf(sources)
  const areaViews: readonly NotificationView[] = Object.freeze(areas.map(areaView))
  return Object.freeze({
    areas,
    prefKeys,
    views: Object.freeze([...INBOX_VIEWS, ...areaViews]),
    viewGroups: Object.freeze([
      { groupName: T.INBOX, views: INBOX_VIEWS },
      { groupName: T.BY_TYPE, views: areaViews },
    ]),
  })
}

const WITHOUT_OPTIMIZER = liveSetOf(LIVE_SOURCES, LIVE_PREF_KEYS)
const WITH_OPTIMIZER = liveSetOf(OPTIMIZER_SOURCES, OPTIMIZER_PREF_KEYS)

// Both variants are built once, so reading this per render hands back a stable identity.
export const notificationLiveSet = (isOptimizerEnabled: boolean): NotificationLiveSet =>
  isOptimizerEnabled ? WITH_OPTIMIZER : WITHOUT_OPTIMIZER

export const NOTIFICATION_VIEW_PARAM = 'view'

/**
 * The row to open on arrival. Clicking a notification in the bell is a request to read *that* one, so the
 * page is told which — through the URL rather than shared state, which also makes the deep link shareable.
 */
export const NOTIFICATION_FOCUS_PARAM = 'notification'

/**
 * Where a notification goes when it is clicked, from the bell or from a toast: the inbox with that row
 * named, so the page can scroll to it and open it. No view filter — a filtered view could exclude the very
 * row being opened.
 */
export const notificationFocusUrl = (id: string): string =>
  `${routes.app.notifications}?${new URLSearchParams({ [NOTIFICATION_FOCUS_PARAM]: id })}`

/** Favouritable URL for a view, so the drawer's star can pin a landing page. */
export const viewUrl = (viewKey: string): string =>
  viewKey === NOTIFICATION_VIEW.ALL ? routes.app.notifications : `${routes.app.notifications}?${NOTIFICATION_VIEW_PARAM}=${viewKey}`

export const viewForKey = (key: string, views: readonly NotificationView[]): NotificationView =>
  views.find(view => view.key === key) ?? views[0]

export const customerLabelFor = (notification: Notification): string | undefined => notification.entityLabel ?? notification.entityId

export type NotificationEmptyStateCopy = { title: string; description: string }

/**
 * What an empty list means depends on why it is empty: a filter that matched nothing is a dead end the
 * user can back out of, an empty Unread view is good news, and an empty inbox is neither. Saying the same
 * thing in all three would leave the reader guessing which one they are looking at.
 */
export const emptyStateFor = ({
  viewKey,
  viewTitle,
  isFiltered,
}: {
  viewKey: string
  viewTitle: string
  isFiltered: boolean
}): NotificationEmptyStateCopy => {
  if (isFiltered) {
    return { title: T.NOTIFICATIONS_EMPTY_FILTERED_TITLE, description: T.NOTIFICATIONS_EMPTY_FILTERED_DESCRIPTION }
  }
  if (viewKey === NOTIFICATION_VIEW.UNREAD) {
    return { title: T.NOTIFICATIONS_EMPTY_STATE_TITLE, description: T.NOTIFICATIONS_EMPTY_STATE_DESCRIPTION }
  }
  if (viewKey.startsWith('AREA_')) {
    return { title: `${T.NOTIFICATIONS_EMPTY_IN_AREA} ${viewTitle}`, description: T.NOTIFICATIONS_EMPTY_AREA_DESCRIPTION }
  }
  return { title: T.NOTIFICATIONS_EMPTY_YET_TITLE, description: T.NOTIFICATIONS_EMPTY_YET_DESCRIPTION }
}

/**
 * The line under the title. A run's counts are columns on the row, so the summary is composed here
 * rather than read out of the producer's sentence — the wording is the client's to own, and the
 * server's `body` stays the fallback for producers with no count to phrase.
 */
export const bodyFor = (notification: Notification): string | undefined => {
  const { source, status, succeededCount, body } = notification
  if (source !== NOTIFICATION_SOURCE.ACCOUNT_DEACTIVATION || status !== NOTIFICATION_STATUS.SUCCEEDED) return body
  if (succeededCount === undefined) return body
  return `${succeededCount} ${succeededCount === 1 ? T.LOCATION_DEACTIVATED_SINGLE : T.LOCATION_DEACTIVATED_PLURAL}`
}

/**
 * Where an entity reference leads, or nothing when it leads nowhere yet — the account page is the
 * only destination that exists today, and a reference with no page reads better as text than as a
 * link that goes nowhere. A deactivated account still has a page: it renders with its indicator.
 */
export const urlForEntity = (
  entityType?: NotificationEntityType,
  entityId?: string,
  jobId?: string,
  dayOfWeek?: string
): string | undefined => {
  if (!entityId) return undefined
  if (entityType === NOTIFICATION_ENTITY_TYPE.ACCOUNT) return getCustomerDetailsPageUrl(entityId)
  // A route lands on its board; the job id rides along so that run's comparison can reopen there.
  if (entityType === NOTIFICATION_ENTITY_TYPE.ROUTE) return getMasterSequencePageUrl(entityId, jobId, dayOfWeek)
  return undefined
}

export const progressFor = (notification: Notification): number | undefined => {
  const { succeededCount, failedCount, totalCount } = notification
  if (!totalCount) return undefined
  const done = (succeededCount ?? 0) + (failedCount ?? 0)
  return Math.round((done / totalCount) * 100)
}

export const NOTIFICATION_FILTER_FIELD = {
  AREA: 'area',
  CATEGORY: 'category',
  STATUS: 'status',
  READ: 'read',
  /** Text: the contract has one `search` across title, body, entity and source — not a per-field match. */
  TEXT: 'title',
} as const

export const READ_STATE = {
  UNREAD: 'UNREAD',
  READ: 'READ',
} as const

/**
 * The dimension a view already pins. That column's filter is hidden while the
 * view is active — offering it again invites a contradictory second filter.
 */
export const fixedFilterFieldFor = (viewKey: string): string | undefined => {
  if (viewKey.startsWith('AREA_')) return NOTIFICATION_FILTER_FIELD.AREA
  if (viewKey.startsWith('CATEGORY_')) return NOTIFICATION_FILTER_FIELD.CATEGORY
  if (viewKey === NOTIFICATION_VIEW.UNREAD) return NOTIFICATION_FILTER_FIELD.READ
  return undefined
}
